// Pure config for travel-guide pages. Zero imports allowed in this file.
// Reviewed facts: Riverside uses Namanga for all scheduled shuttles;
// typical crossing 15–45 min but delays vary; passengers clear immigration
// in person; visa/health rules change — verify before travel; COVID certificates
// generally no longer mandatory but health screening may change; yellow fever
// evidence may be required depending on travel history/nationality;
// Isebania is relevant only to occasional private itineraries.

export const GUIDE_CONFIGS = [
  {
    slug: 'border-crossing',
    kind: 'guide',
    breadcrumbLabel: 'Namanga Border Crossing Guide',
    h1: 'Crossing the Kenya–Tanzania Border at Namanga',
    title: 'Namanga Border Crossing Guide | Riverside Shuttle',
    metaDescription:
      'Practical guide to crossing the Kenya–Tanzania border at Namanga: what to expect at immigration, documents to carry, visa links, health requirements, and how to book your shuttle.',
    lastReviewed: 'July 2026',
    disclaimer:
      'Visa, health, and border rules change. Verify current requirements with the official Kenya and Tanzania immigration authorities before you travel. This guide is informational only.',
    sections: [
      {
        heading: 'Which border does Riverside use?',
        body:
          'All Riverside scheduled shuttles cross at Namanga, the main road border post on the Nairobi–Arusha highway. Namanga has dedicated immigration halls for both Kenya and Tanzania and is the standard overland crossing for this corridor. Isebania, in western Kenya, is a different crossing entirely — it is not used on any Riverside scheduled service and is only relevant to occasional bespoke private itineraries.',
      },
      {
        heading: 'How long does the crossing take?',
        body:
          'Under normal conditions, clearing Kenyan exit immigration and Tanzanian entry immigration takes roughly 15–45 minutes. That time can stretch considerably during busy travel periods, public holidays, or when systems are slow. Build buffer into your day and avoid tight onward connections on the same afternoon. Journey times quoted for each route already include the border stop.',
      },
      {
        heading: 'What happens step by step?',
        body:
          'The shuttle stops at the Kenyan border post. You collect your carry-on, walk across to the Kenyan immigration hall, receive your exit stamp, then continue on foot to the Tanzanian immigration hall. There you present your passport, visa or eTA, and any required health documents. Once stamped in, you walk to the shuttle on the Tanzanian side. The driver waits for all passengers before continuing to Arusha or Moshi — you do not need to board a different vehicle.',
      },
      {
        heading: 'Documents to have ready',
        checklist: [
          'Passport valid for at least 6 months beyond your intended stay',
          'Tanzania visa if required for your nationality — apply online for an eVisa before you travel (recommended), or get a visa on arrival at Namanga if your nationality is eligible',
          'Kenya eTA if required (relevant for Arusha-to-Nairobi direction) — best done online before you travel, though it can also be completed at Namanga',
          'Yellow fever vaccination certificate if arriving from or transiting through a country with yellow fever risk — visitors who have been more than 12 hours in Nairobi will need a certificate unless medically exempt; verify requirements before travel',
          'Any other health documentation required at the time of travel',
          'Booking confirmation or e-ticket to show the driver',
        ],
      },
      {
        heading: 'Visa and entry requirements',
        body:
          "Entry rules vary by nationality and change. Tanzania's online visa is the eVisa: most travellers apply for it before travelling, and eligible nationalities can instead get a visa on arrival at Namanga. Kenya operates an eTA system for most foreign visitors: apply online before you travel if you can, though in practice it can also be completed at Namanga. Do not rely on second-hand or out-of-date information — check directly with the official portals below before booking your travel.",
        externalLinks: [
          {
            label: 'Tanzania eVisa — official application portal',
            href: 'https://visa.immigration.go.tz/',
          },
          {
            label: 'Tanzania visa guidelines — who is eligible for which visa',
            href: 'https://visa.immigration.go.tz/guidelines',
          },
          {
            label: 'Kenya eTA — official application portal',
            href: 'https://etakenya.go.ke/',
          },
        ],
      },
      {
        heading: 'Health requirements',
        body:
          'COVID-19 vaccination certificates and pre-departure tests are generally no longer required at the Namanga crossing, but health screening procedures and entry health rules can change at short notice. Yellow fever vaccination evidence may be required if you are travelling from or through a country considered to have a yellow fever transmission risk. Visitors who have been more than 12 hours in Nairobi will need a yellow fever vaccination certificate unless medically exempt. Check current health requirements with the relevant embassy or official health authority before you travel.',
      },
    ],
    faqs: [
      {
        q: 'Do I need a visa to enter Tanzania from Kenya?',
        a: 'Most nationalities require a Tanzania visa. You can apply online for an eVisa before you travel, which we recommend, or get a visa on arrival at Namanga if your nationality is eligible. Verify your specific requirements with the official Tanzania immigration authority — rules and eligible nationalities change.',
      },
      {
        q: "Does Riverside's driver help me at the border?",
        a: 'Your driver accompanies the group to the border, guides you through the process, and waits on the Tanzanian side while you clear immigration. The actual immigration clearance — presenting your passport and documents — is done by you in person.',
      },
      {
        q: 'How long should I budget for the crossing?',
        a: 'Plan for 15–45 minutes under normal conditions, but queues during busy periods or system delays can push this further. Shuttle journey times already account for a standard border stop.',
      },
      {
        q: 'Is a yellow fever certificate required?',
        a: 'Yellow fever vaccination evidence may be required if you are travelling from or transiting through a country with a risk of yellow fever transmission. Visitors who have been more than 12 hours in Nairobi will need a yellow fever vaccination certificate unless medically exempt. Requirements vary by nationality and itinerary, and can change — verify with the embassy or official health authority before travel.',
      },
      {
        q: 'Are COVID-19 certificates still required?',
        a: 'COVID-19 vaccination certificates and pre-departure tests are generally no longer required at Namanga, but health entry rules can change. Always check the current requirements before you travel.',
      },
      {
        q: 'What if I cross at Isebania instead?',
        a: 'Isebania is a Kenya–Tanzania border crossing in western Kenya, far from both Nairobi and Arusha. Riverside scheduled shuttles do not use it. It may be relevant to a bespoke private itinerary routing via western Kenya — contact us if you need that.',
      },
    ],
    busCtaHref: '/bus',
    privateCtaHref: '/private-transfer',
    busCtaLabel: 'Book a shuttle',
    privateCtaLabel: 'Request a private transfer',
  },
  // Ported from tickets.riverside-shuttle.com (post 17255, published 6 Jan 2026),
  // kept close to word for word so its search ranking carries across the 301.
  // Facts brought in line with busScheduleMeta.js, routes.js and the booking
  // conditions on 22 Oct 2026: fares from £28 (was £16), arrival 13:40, 5½–6 h,
  // pickup 07:00/13:00, Parkside 07:25/13:25, JKIA Paul Cafe 20 min before,
  // private transfer by quote. Entry and payment facts corrected 22 Oct 2026:
  // Tanzania eVisa or visa on arrival at Namanga (the eTA is Kenya's), Airtel Money
  // via TZS only (KES / M-Pesa not working). PayPal added once live (bus + private).
  {
    slug: 'nairobi-to-arusha-travel-guide',
    kind: 'guide',
    breadcrumbLabel: 'Nairobi to Arusha Travel Guide',
    h1: "How to get from Nairobi to Arusha in 2026: The Complete Traveler's Guide",
    title: 'How to Get from Nairobi to Arusha in 2026 | Riverside Shuttle',
    metaDescription:
      'Discover the best way to get from Nairobi to Arusha in 2026—daily shuttle, hotel transfers, and border assistance all included',
    lastReviewed: 'October 2026',
    heroImage: {
      src: '/images/guides/nairobi-to-arusha/nairobi-to-arusha-guide-2026.webp',
      alt: 'Illustration of a shuttle bus travelling from Nairobi towards Mount Kilimanjaro: how to get from Nairobi to Arusha in 2026',
      width: 1200,
      height: 801,
    },
    // JPEG copy of the hero for link previews (WhatsApp is unreliable with WebP).
    socialImage: '/images/guides/nairobi-to-arusha/nairobi-to-arusha-guide-2026.jpg',
    intro: [
      'Planning a trip from Nairobi to Arusha? You’re in for an amazing journey through East Africa’s stunning landscapes. Whether you’re heading to Tanzania for a safari adventure, climbing Kilimanjaro, or exploring the cultural heritage of Arusha, getting there from Nairobi has never been easier or more affordable.',
      '**Bus shuttle service is the most reliable and budget-friendly way to travel the approximately 274 kilometers between these two vibrant cities.** The journey takes you through beautiful countryside, past traditional villages, and across the famous Namanga border crossing – all while enjoying comfortable transport and professional service.',
      '**Private Car/Driving**',
      'If you prefer complete control over your schedule and a little extra comfort, driving takes approximately 4½–5 hours door to door. Car hire prices vary; our [private transfer service](/nairobi-to-arusha-private-transfer) gives you the whole vehicle, priced by instant online quote.',
      '**Flying**',
      'Commercial flights are available but are rarely used for this short distance. When you factor in airport arrival times, check-in procedures, transport to and from the airport, the relatively short flight time is rarely worth the hassle, shuttles prove much more practical and economical.',
    ],
    sections: [
      {
        heading: 'Why Choose Bus Transport for Your Nairobi-Arusha Journey',
        blocks: [
          { p: '**Bus shuttle service offers unbeatable value and convenience** compared to other transportation options. While flying might seem faster, when you factor in airport transfers, check-in times, and costs, our direct shuttle service often proves more efficient and significantly more economical.' },
          { p: '**Our Toyota Coaster 22-seater buses provide the perfect balance of comfort and affordability.** These modern vehicles are specifically designed for long-distance travel in East Africa, featuring comfortable seating, air conditioning, and ample storage space for your luggage.' },
          { img: { src: '/images/guides/nairobi-to-arusha/shuttle-bus-nairobi-arusha-highway.webp', alt: 'Toyota Coaster shuttle bus on the Nairobi to Arusha highway below the Rift Valley escarpment', width: 1200, height: 671 } },
          { h3: 'Key Advantages of Shuttle Bus Travel:' },
          {
            ul: [
              '**No airport hassles** – Door-to-door service from your Nairobi hotel to your Arusha destination',
              '**Scenic route** – Enjoy stunning views of the Great Rift Valley and Mount Kilimanjaro',
              '**Border crossing assistance**',
              '**Flexible luggage allowance** – Generous space for safari gear and souvenirs',
              '**Daily departures** – Multiple options to fit your schedule',
            ],
          },
        ],
      },
      {
        heading: 'Riverside Shuttle: Your Trusted Travel Partner',
        blocks: [
          { p: 'At **Riverside Shuttle, we specialise in comfortable, reliable transport between Kenya and Tanzania**. Our service has been connecting travelers with their East African adventures for years, and we understand exactly what you need for a smooth, stress-free journey.' },
          { h3: 'Our Pricing: Fares from £28 (about $37)' },
          { p: '**Our competitive fares start from £28 (about $37)**, making us one of the most affordable options for quality transport between Nairobi and Arusha. This incredible value includes:' },
          {
            ul: [
              'Professional driver service',
              'Border crossing assistance',
              'Hotel pickup in Nairobi',
              'Comfortable seating in our Toyota Coaster vehicles',
              'Generous luggage allowance',
            ],
          },
          { p: '**Book early to secure your seat** on your preferred departure date. Our commitment to affordable, quality service remains constant.' },
          { img: { src: '/images/guides/nairobi-to-arusha/shuttle-window-view-rift-valley.webp', alt: 'View from the shuttle bus window of acacia trees and the Rift Valley escarpment on the way to Arusha', width: 1200, height: 800 } },
        ],
      },
      {
        heading: 'Travel Schedule and Journey Time',
        blocks: [
          { p: '**We operate daily departures with two convenient time slots:**' },
          {
            ul: [
              '**Morning departure:** 08:00 from Nairobi, arriving in Arusha at around 13:40',
              '**Afternoon departure:** 14:00 from Nairobi, arriving in Arusha at around 19:30',
            ],
          },
          { p: '**The total journey time is approximately 5½–6 hours**, including border crossing procedures. This timing accounts for:' },
          {
            ul: [
              'Pickup from Nairobi hotels (07:00 for the morning departure, 13:00 for the afternoon departure)',
              'Travel to Namanga border',
              'Immigration and customs procedures',
              'Rest stop shortly after the border crossing',
              'Final leg to Arusha with drop-off at your accommodation',
            ],
          },
          { h3: 'What to Expect During Your Journey' },
          { p: '**Your comfort is our priority throughout the entire trip.** Our Toyota Coaster buses are equipped with air conditioning, comfortable reclining seats, and large windows perfect for photographing the spectacular scenery along the way.' },
          { p: '**Rest stops are strategically planned** to ensure you can stretch your legs, grab refreshments, and use clean facilities. Our drivers are experienced professionals who know the route intimately and ensure a smooth, safe journey.' },
        ],
      },
      {
        heading: 'Comprehensive Service Inclusions',
        blocks: [
          { h3: 'Hotel Pickup and Drop-off Service' },
          { p: '**We provide convenient hotel pickup in Nairobi** at 07:00 for our 08:00 departure and 13:00 for our 14:00 departure. Our pickup service covers most hotels in Nairobi city center and nearby areas. **Our main station is located at Parkside Hotel**, but we’ll collect you from your accommodation to save you the hassle of getting to our departure point. If you’d rather meet us there, be at Parkside Hotel by 07:25 for the 08:00 departure or 13:25 for the 14:00 departure.' },
          { p: '**In Arusha, we provide drop-off service** to hotels and lodges within the city center. If you’re staying outside the main area, we’ll arrange the most convenient drop-off point and can help coordinate onward transport.' },
          { img: { src: '/images/guides/nairobi-to-arusha/namanga-border-crossing-illustration.webp', alt: 'Illustration of shuttle passengers walking between the Kenya and Tanzania border posts at Namanga', width: 1024, height: 1024 } },
          { h3: 'Generous Luggage Allowance' },
          { p: '**Each passenger is entitled to:**' },
          {
            ul: [
              '**2 main bags** (maximum 30kg each)',
              '**1 backpack or carry-on**',
              '**Additional small personal items**',
            ],
          },
          { p: 'This generous allowance means you can pack everything you need for your safari, trekking adventure, or extended stay in Tanzania without worrying about excess baggage fees. You could even bring your bike – just let us know in advance.' },
          { h3: 'The Namanga Border Crossing' },
          { p: '**The Namanga border crossing may initially appear daunting for first-time travelers**, but it’s really fairly straightforward. Our experienced drivers will point you in the right direction at the border. **We allow time for all passengers** to go through immigration and customs procedures. The border procedures can take from between 15 - 45 minutes depending on the day.' },
          { p: '**Essential documents you’ll need:**' },
          {
            ul: [
              'Valid passport (minimum 6 months validity)',
              'Tanzania visa if required for your nationality – apply online for an eVisa before you travel (recommended), or get a visa on arrival at Namanga if your nationality is eligible',
              'Yellow fever certificate (if traveling from yellow fever endemic areas, or if you have spent more than 12 hours in Nairobi – check the official requirements before you travel)',
            ],
          },
          { p: 'For further advice regarding the crossing: [Read Here](/border-crossing)' },
        ],
      },
      {
        heading: 'Airport Connections and Special Services',
        blocks: [
          { h3: 'Jomo Kenyatta International Airport (JKIA) Transfers' },
          { p: '**Need to catch a flight from JKIA?** We offer flexible connections to Nairobi’s main airport. Our [airport service](/nairobi-airport-to-arusha-shuttle) leaves at 08:30 and 14:30 from Paul Cafe, outside Terminal 1 arrivals – please be there 20 minutes before departure – and we have a team in the airport to assist you.' },
          { h3: 'Arusha and Kilimanjaro Airport Options' },
          { p: '**While we don’t provide direct service to Arusha Airport or Kilimanjaro International Airport**, we can drop you at convenient locations where taxi connections are readily available. **For Kilimanjaro International Airport specifically**, we recommend booking to/or from Moshi, where we drop you at a convenient point to take a short taxi ride from there.' },
          { p: 'Alternatively we can arrange a [private transfer](/private-transfer) directly there.' },
          { p: '**For travelers with specific airport requirements**, our team can arrange specialized transport solutions to meet your needs.' },
          { img: { src: '/images/guides/nairobi-to-arusha/jkia-airport-shuttle-illustration.webp', alt: 'Illustration of the airport shuttle outside Jomo Kenyatta International Airport (JKIA) in Nairobi', width: 1024, height: 1024 } },
        ],
      },
      {
        heading: 'Easy Online Booking Process',
        blocks: [
          { p: '**Booking your Nairobi to Arusha journey is simple and secure** through our online platform. Visit [riverside-shuttle.com](/bus) to:' },
          {
            ul: [
              'Check real-time availability',
              'Select your preferred departure time',
              'Choose your pickup location in Nairobi',
              'Specify your Arusha drop-off point',
              'Make secure payment online',
            ],
          },
          { h3: 'Multiple Payment Options' },
          { p: '**We accept various payment methods** to make booking convenient for international travelers:' },
          {
            ul: [
              'Major credit and debit cards (Visa, Mastercard) through secure card checkout',
              'PayPal at checkout',
              'Airtel Money – switch the currency to TZS on the booking form (M-Pesa is not available yet)',
            ],
          },
          { p: '**Book your Nairobi to Arusha ticket** by visiting our [main booking page](/nairobi-to-arusha-shuttle) where you can see current availability and secure your seat instantly.' },
        ],
      },
      {
        heading: 'Essential Travel Tips for Your Journey',
        blocks: [
          { h3: 'What to Pack for the Trip' },
          { p: '**Dress comfortably for the 5-6 hour journey** with layers to accommodate changing temperatures. The route takes you from Nairobi’s higher altitude to lower, warmer areas near the border, then back to Arusha’s moderate climate.' },
          { p: '**Essential items to carry:**' },
          {
            ul: [
              'Valid travel documents in easily accessible bag',
              'Snacks and water for the journey',
              'Camera for spectacular scenery shots',
              'Light jacket for air-conditioned bus',
              'Small amount of local currency or USD for border fees',
            ],
          },
          { h3: 'Health and Safety Considerations' },
          { p: '**We prioritise your health and safety** throughout the journey. Our vehicles undergo regular maintenance, and our drivers are professionally trained for long-distance travel in East African conditions.' },
          { p: '**Health precautions to consider:**' },
          {
            ul: [
              'Ensure vaccinations are up to date',
              'Carry any personal medications',
              'Stay hydrated during travel',
              'Use sunscreen for window-side seating',
            ],
          },
          { img: { src: '/images/guides/nairobi-to-arusha/shuttle-bus-road-to-kilimanjaro.webp', alt: 'Aerial view of a shuttle bus on the road through the savannah towards Mount Kilimanjaro', width: 1152, height: 768 } },
        ],
      },
      {
        heading: 'Why Riverside Shuttle Is Your Best Choice',
        blocks: [
          { p: '**Our reputation is built on reliability, comfort, and exceptional customer service.** We understand that your journey from Nairobi to Arusha is often the beginning of a once-in-a-lifetime East African adventure, and we’re committed to starting that experience on the right note.' },
          { p: '**What sets us apart:**' },
          {
            ul: [
              '**Daily reliable departures** – We operate every day of the year',
              '**Professional drivers** with extensive cross-border experience',
              '**Modern Toyota Coaster fleet** maintained to international standards',
              '**Multilingual staff** to assist international travelers',
              '**Flexible booking options** – free date changes up to 12 hours before departure and a clear [cancellation policy](/booking-conditions)',
            ],
          },
          { p: '**Ready to book your journey?** Visit our [booking platform](/bus) today to secure your seat on our comfortable, reliable shuttle service. With departure times at 08:00 and 14:00 daily, and fares from £28 (about $37), your East African adventure begins with Riverside Shuttle.' },
          { p: '**Contact our customer service team** if you have any questions about luggage, pickup locations, or special requirements. We’re here to ensure your journey from Nairobi to Arusha is comfortable, affordable, and memorable for all the right reasons.' },
        ],
      },
    ],
    faqs: [
      {
        q: 'How long does the shuttle from Nairobi to Arusha take?',
        a: 'About 5½–6 hours, including the Namanga border crossing. The 08:00 departure arrives in Arusha at around 13:40 and the 14:00 departure at around 19:30.',
      },
      {
        q: 'How much is the shuttle from Nairobi to Arusha?',
        a: 'Fares start from £28 (about $37) per person, including hotel pickup in Nairobi, border crossing assistance and a luggage allowance of 2 bags up to 30kg plus a backpack.',
      },
      {
        q: 'What time is hotel pickup in Nairobi?',
        a: 'Be ready at your hotel by 07:00 for the 08:00 departure, or 13:00 for the 14:00 departure. If you meet us at Parkside Hotel instead, be there by 07:25 or 13:25.',
      },
      {
        q: 'Where do I meet the shuttle at JKIA?',
        a: 'At Paul Cafe, outside Terminal 1 arrivals. The airport shuttle leaves at 08:30 and 14:30; please be there 20 minutes before departure.',
      },
      {
        q: 'Do I need a visa to travel from Nairobi to Arusha?',
        a: 'Most nationalities need a Tanzania visa. You can apply online for an eVisa before you travel, which we recommend, or get a visa on arrival at Namanga if your nationality is eligible. See our Namanga border crossing guide for documents and official links.',
      },
    ],
    busCtaHref: '/nairobi-to-arusha-shuttle',
    privateCtaHref: '/nairobi-to-arusha-private-transfer',
    busCtaLabel: 'Book the Nairobi–Arusha shuttle',
    privateCtaLabel: 'Request a private transfer',
  },
  // Replaces the old tickets. product "Kenya to Tanzania baggage transfer"
  // (post 12737). Content comes from that product only; no prices or opening
  // hours, which are unconfirmed. Not bookable online yet: contactOffices swaps
  // the booking buttons for the office numbers in offices.js.
  {
    slug: 'baggage-and-parcel-transfer',
    kind: 'guide',
    breadcrumbLabel: 'Baggage & parcel transfer',
    h1: 'Kenya to Tanzania baggage & parcel transfer',
    title: 'Baggage & Parcel Transfer: Nairobi, Arusha, Moshi | Riverside Shuttle',
    metaDescription:
      'Riverside baggage and parcel transfer between Nairobi, Arusha and Moshi. We carry your bags or items between our offices in Kenya and Tanzania, with storage for up to two months.',
    intro: [
      'Our trusted baggage and parcel transfer service runs between the Kenyan and Tanzanian cities of **Nairobi, Arusha and Moshi**. Your bags or items travel on our daily shuttle from one of our offices to another, so you don’t need to travel with them.',
      'We can also **store your bags for up to two months** until you collect them.',
    ],
    contactOffices: true,
    contactCtaLabel: 'Call or WhatsApp to arrange',
    contactHeading: 'Arrange a baggage or parcel transfer',
    contactText:
      'Online booking for this service is coming soon. For now, call one of our offices or message the UK booking desk on WhatsApp, and tell us where you’re sending from and to, and how many bags.',
    sections: [
      {
        heading: 'How it works',
        blocks: [
          {
            ul: [
              '**Drop off** your bag or parcel at our office in the city it’s leaving from.',
              '**It travels on our scheduled shuttle** to the destination city.',
              '**Collect it** from our office there, straight away or after a period of storage.',
            ],
          },
          { p: 'Bags and parcels must be delivered to, and collected from, our offices.' },
        ],
      },
      {
        heading: 'Routes',
        blocks: [
          {
            ul: [
              'Nairobi ↔ Arusha',
              'Nairobi ↔ Moshi',
              'Arusha ↔ Moshi',
            ],
          },
        ],
      },
      {
        heading: 'Storage',
        blocks: [
          {
            p: 'Heading off on safari or up Kilimanjaro? We can hold your bags at the destination office for **up to 1 week, 2 weeks, 1 month or 2 months** until you’re ready to collect them.',
          },
        ],
      },
      {
        heading: 'Pricing',
        blocks: [
          {
            p: 'Priced per bag weighing up to 30 kg, depending on the route and how long we store it. For several bags, or anything larger, get in touch and we’ll quote.',
          },
        ],
      },
      {
        heading: 'Where to drop off and collect',
        blocks: [
          {
            ul: [
              '**Nairobi:** Lagos House, 1st Floor, Room 1, Jivanjee Gardens, Monrovia Street',
              '**Arusha:** ACU Building, Ground Floor, Room 2, Sokoine Road',
              '**Moshi:** YMCA Building, Boma Road. Call our Tanzania office to arrange.',
            ],
          },
        ],
      },
    ],
    faqs: [
      {
        q: 'Do I need to travel with my bag?',
        a: 'No. You drop it at our office and it travels on our scheduled shuttle to the destination office, where you collect it.',
      },
      {
        q: 'Which cities do you carry bags and parcels between?',
        a: 'Nairobi, Arusha and Moshi, in either direction.',
      },
      {
        q: 'Can you store my bag while I’m on safari or climbing Kilimanjaro?',
        a: 'Yes. We can hold bags at the destination office for up to 1 week, 2 weeks, 1 month or 2 months.',
      },
      {
        q: 'How much does it cost?',
        a: 'It’s priced per bag up to 30 kg, depending on the route and storage time. Call or WhatsApp us for a price, especially for several bags.',
      },
      {
        q: 'Can I book online?',
        a: 'Not yet. Online booking is coming soon. For now, call one of our offices or message our UK booking desk on WhatsApp.',
      },
    ],
  },
];

export function getGuideBySlug(slug) {
  return GUIDE_CONFIGS.find((g) => g.slug === slug) || null;
}
